// src/App.js - WITH DUAL SYSTEM ARCHITECTURE AND I18N

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChatProvider } from './contexts/ChatContext';
import { SettingsProvider } from './contexts/SettingsContext';
import './i18n/i18n';
import './styles/variables.css';

// Componentes
import { NotificationProvider } from './contexts/NotificationContext';
import useAuth from './hooks/useAuth';
import presenceService from './services/PresenceService';
import { CallProvider } from './contexts/CallContext';
import CallModal from './components/global/Call/CallModal';

// Páginas
import SystemAwareProtectedRoute from './components/security/SystemAwareProtectedRoute';
import LoginPage from './modules/Login/LoginPage';
import DashboardPage from './modules/Dashboard/DashboardPage';
import NotFoundPage from './components/global/NotFound/NotFoundPage';

// ============================================
// TUV SYSTEM PAGES - NEW MODULES
// ============================================
import TUVDashboard from './modules/TUV/TUVDashboard';
import TUVReportPage from './modules/TUV/TUVReportPage';
import TUVChecklistModelPage from './modules/TUV/TUVChecklistModelPage';
import TUVChecklistPage from './modules/TUV/TUVChecklistPage';

// Módulo HelpDesk
import HelpDeskPage from './modules/HelpDesk/HelpDeskPage';
import TicketsTIPage from './modules/HelpDesk/TicketsTIPage';

/* Module Parameters */
import ProductCategoryPage from './modules/Parameters/ProductCategoryPage';
import BankPage from './modules/Parameters/BankPage';
import SubCategoryPage from './modules/Parameters/SubCategoryPage';
import InventoryPage from './modules/Parameters/InventoryPage';
import PackagingBoxPage from './modules/Parameters/PackagingBoxPage';
import RegionPage from './modules/Parameters/RegionPage';
import UnitOfMeasurePage from './modules/Parameters/UnitOfMeasurePage';
import RolesPage from './modules/Parameters/RolesPage';
import BillingConditionPage from './modules/Parameters/BillingConditionPage';
import CheckStatusPage from './modules/Parameters/CheckStatusPage';
import TargetAudiencePage from './modules/Parameters/TargetAudiencePage';
import ProjectModelPage from './modules/Parameters/ProjectModelPage';
import ColorsPage from './modules/Parameters/ColorsPage';
import QualityCriteriaPage from './modules/Parameters/QualityCriteriaPage';
import PackagingPage from './modules/Parameters/PackagingPage';
import ContactReasonSACPage from './modules/Parameters/ContactReasonSACPage';
import SituationSACPage from './modules/Parameters/SituationSACPage';
import ProductReturnPage from './modules/Parameters/ProductReturnPage';
import ModelsPage from './modules/Parameters/ModelsPage';
import StockChangeReasonPage from './modules/Parameters/StockChangeReasonPage';
import SuffixPage from './modules/Parameters/PrefixPage';
import PreparationSetupPage from './modules/Parameters/PreparationSetup';

import TypeClientPage from './modules/Parameters/Tipos/TypeClientPage';
import TypeProductPage from './modules/Parameters/Tipos/TypeProductPage';
import TypeAddressPage from './modules/Parameters/Tipos/TypeAddressPage';
import TypePhonePage from './modules/Parameters/Tipos/TypePhonePage';
import TypeContactPage from './modules/Parameters/Tipos/TypeContactPage';
import TypeContactResultPage from './modules/Parameters/Tipos/TypeContactResultPage';
import TypePaymentDocPage from './modules/Parameters/Tipos/TypePaymentDocPage';
import TypeProjectPage from './modules/Parameters/Tipos/TypeProjectPage';
import TypePositionPage from './modules/Parameters/Tipos/TypePositionPage';
import TypeNaturePage from './modules/Parameters/Tipos/TypeNaturePage';
import TypeBusinessPage from './modules/Parameters/Tipos/TypeBusinessPage';
import TypeBoxPage from './modules/Parameters/Tipos/TypeBoxPage';
import TypePalletPage from './modules/Parameters/Tipos/TypePalletPage';

/* Modules de Registers */
import RawMaterialsPage from './modules/Registers/RawMaterialsPage';
import ProductsPage from './modules/Registers/ProductsPage';
import SupplierPage from './modules/Registers/SupplierPage';
import ProceduresPage from './modules/Registers/ProceduresPage';
import ClientsPage from './modules/Registers/ClientsPage';
import AccountExecutivePage from './modules/Registers/AccountExecutivePage';
import ShippingPage from './modules/Registers/ShippingPage';

/*Module Projects*/
import DevelopmentPage from './modules/Projects/DevelopmentPage';
import ProjectStatusPage from './modules/Projects/ProjectStatusPage'
import SampleRequestPage from './modules/Projects/SampleRequestPage';

/* Module Orders */
import SaleOrderPage from './modules/Orders/SaleOrderPage';
import PurchaseOrderPage from './modules/Orders/PurchaseOrderPage';
import SaleOrderApprovalPage from './modules/Orders/SaleOrderApprovalPage';
import OrderReturnAnalysisPage from './modules/Orders/OrderReturnAnalysisPage';
import PurchaseOrderApprovalPage from './modules/Orders/PurchaseOrderApprovalPage';
import PurchaseOrderEntryPage from './modules/Orders/PurchaseOrderEntryPage';
import PurchaseRequestPage from './modules/Orders/PurchaseRequestPage';

/* Module Production */
import ProductionOrderPage from './modules/Production/ProductionOrderPage';
import ProductionPhasePage from './modules/Production/ProductionPhasePage';
import BarCodeEmissionPage from './modules/Production/BarCodeEmissionPage';
import ForecastPage from './modules/Production/ForecastPage';

/* Module Stock */
import LocationRegisterPage from './modules/Stock/LocationRegisterPage';
import StockEntryPage from './modules/Stock/StockEntryPage';
import StockOutflowPage from './modules/Stock/StockOutflowPage';
import StockQueryPage from './modules/Stock/StockQueryPage';

/* Module Tax */
import NCMRegisterPage from './modules/Tax/NCMRegisterPage';
import CFOPRegisterPage from './modules/Tax/CFOPRegisterPage';
import RecipientManifestPage from './modules/Tax/RecipientManifestPage';
import InvoiceIssuingPage from './modules/Tax/InvoicingIssuingPage';

/* Module Financial */
import CostCenterPage from './modules/Financial/CostCenterPage';
import AccountPlanPage from './modules/Financial/AccountPlanPage';

/* Module Quality Control */
import PackagingAnalysisPage from './modules/Quality/PackagingAnalysisPage';

/* Module reports */
import StockReportPage from './modules/Reports/StockReportPage';
import UserReportPage from './modules/Reports/UserReportPage';
import ClientListReportPage from './modules/Reports/ClientListReportPage';
import SupplierListReportPage from './modules/Reports/SupplierListReportPage';
import CarrierListReportPage from './modules/Reports/CarrierListReportPage';
import ProductRecordReportPage from './modules/Reports/ProductRecordReportPage';
import ProductReportPage from './modules/Reports/ProductReportPage';
import SampleReportPage from './modules/Reports/SampleReportPage';
import HelpDeskReportPage from './modules/Reports/HelpDeskReportPage';
import MaterialBudgetPage from './modules/Reports/MaterialBudgetPage';

/* Import de Usuários */
import UsersPage from './modules/Users/UsersPage';
import PermissionsPage from './modules/Permissions/PermissionsPage';

import './App.css';

function App() {
    const { user } = useAuth();

    // Handle presence service initialization
    useEffect(() => {
        if (user) {
            // Initialize presence when user logs in
            presenceService.initialize(user);
        } else {
            // Clean up presence when user logs out
            presenceService.cleanup();
        }

        // Cleanup on unmount
        return () => {
            presenceService.cleanup();
        };
    }, [user]);

    return (
        <SettingsProvider>
            <CallProvider>
            <Router>
                <NotificationProvider>
                    <ChatProvider>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />

                        {/* ============================================ */}
                        {/* PROTECTED ROUTES WITH DUAL SYSTEM AWARENESS */}
                        {/* ============================================ */}
                        <Route element={<SystemAwareProtectedRoute />}>
                            {/* Root redirect - handled by SystemAwareProtectedRoute based on role */}
                            <Route index element={<Navigate to="/dashboard" replace />} />

                            {/* ============================================ */}
                            {/* BASE ERP SYSTEM ROUTES */}
                            {/* ============================================ */}

                            {/* Dashboard */}
                            <Route path="/dashboard" element={<DashboardPage />} />

                            {/* HelpDesk Module */}
                            <Route path="/helpdesk/central-de-suporte" element={<HelpDeskPage />} />
                            <Route path="/helpdesk/gerenciamento-de-tickets" element={<TicketsTIPage />} />

                            {/* Users & Permissions */}
                            <Route path="/usuarios" element={<UsersPage />} />
                            <Route path="/permissoes" element={<PermissionsPage />} />

                            {/* Cadastros Module */}
                            <Route path="/cadastros/materia-prima" element={<RawMaterialsPage />} />
                            <Route path="/cadastros/produtos" element={<ProductsPage />} />
                            <Route path="/cadastros/fornecedores" element={<SupplierPage />} />
                            <Route path="/cadastros/procedimentos" element={<ProceduresPage />} />
                            <Route path="/cadastros/clientes" element={<ClientsPage />} />
                            <Route path="/cadastros/executivos-de-conta" element={<AccountExecutivePage />} />
                            <Route path="/cadastros/transportadora" element={<ShippingPage />} />

                            {/* Projeto Module */}
                            <Route path="/projeto/desenvolvimento" element={<DevelopmentPage />} />
                            <Route path="/projeto/acompanhamento-de-projeto" element={<ProjectStatusPage />} />
                            <Route path="/projeto/requisicao-de-amostra" element={<SampleRequestPage />} />

                            {/* Pedidos Module */}
                            <Route path="/pedidos/pedido-de-venda" element={<SaleOrderPage />} />
                            <Route path="/pedidos/pedido-de-compra" element={<PurchaseOrderPage />} />
                            <Route path="/pedidos/aprovacao-de-pedido-de-venda" element={<SaleOrderApprovalPage />} />
                            <Route path="/pedidos/aprovacao-de-pedido-de-compra" element={<PurchaseOrderApprovalPage />} />
                            <Route path="/pedidos/entrada-de-pedido-de-compra" element={<PurchaseOrderEntryPage />} />
                            <Route path="/pedidos/requisicao-de-compra" element={<PurchaseRequestPage />} />
                            <Route path="/pedidos/retorno-para-analise" element={<OrderReturnAnalysisPage />}/>

                            {/* Produção Module */}
                            <Route path="/producao/ordens-de-producao" element={<ProductionOrderPage />} />
                            <Route path="/producao/fases-de-producao" element={<ProductionPhasePage />} />
                            <Route path="/producao/emissao-de-codigo-de-barras" element={<BarCodeEmissionPage />} />
                            <Route path="/producao/forecast" element={<ForecastPage />} />

                            {/* Estoque Module */}
                            <Route path="/estoque/embalagens/localizacoes" element={<LocationRegisterPage />} />
                            <Route path="/estoque/embalagens/entrada-de-estoque" element={<StockEntryPage />} />
                            <Route path="/estoque/embalagens/saida-de-estoque" element={<StockOutflowPage />} />
                            <Route path="/estoque/embalagens/consulta-de-estoque" element={<StockQueryPage />} />

                            {/* Financeiro Module */}
                            <Route path="/financeiro/centro-de-custo" element={<CostCenterPage />} />
                            <Route path="/financeiro/plano-de-contas" element={<AccountPlanPage />} />

                            {/* Controle de Qualidade Module */}
                            <Route path="/controle-de-qualidade/analise-de-embalagens" element={<PackagingAnalysisPage />} />

                            {/* Relatórios Module */}
                            <Route path="/relatorios/estoque-atual" element={<StockReportPage />} />
                            <Route path="/relatorios/usuarios" element={<UserReportPage />} />
                            <Route path="/relatorios/clientes" element={<ClientListReportPage />} />
                            <Route path="/relatorios/fornecedores" element={<SupplierListReportPage />} />
                            <Route path="/relatorios/transportadoras" element={<CarrierListReportPage />} />
                            <Route path="/relatorios/ficha-de-produto" element={<ProductRecordReportPage />} />
                            <Route path="/relatorios/produtos" element={<ProductReportPage />} />
                            <Route path="/relatorios/amostras" element={<SampleReportPage />} />
                            <Route path="/relatorios/suporte" element={<HelpDeskReportPage />} />
                            <Route path="/relatorios/materiais" element={<MaterialBudgetPage />} />

                            {/* Fiscal Module */}
                            <Route path="/fiscal/manifesto-do-destinatario" element={<RecipientManifestPage />} />
                            <Route path="/fiscal/cadastro-de-ncm" element={<NCMRegisterPage />} />
                            <Route path="/fiscal/cadastro-de-cfop" element={<CFOPRegisterPage />} />
                            <Route path="/fiscal/emissao-de-nfe" element={<InvoiceIssuingPage/>} />

                            {/* Parâmetros Module */}
                            <Route path="/parametros/categoria-de-produto" element={<ProductCategoryPage />} />
                            <Route path="/parametros/bancos" element={<BankPage />} />
                            <Route path="/parametros/sub-categoria" element={<SubCategoryPage />} />
                            <Route path="/parametros/inventario" element={<InventoryPage />} />
                            <Route path="/parametros/embalagem-caixa" element={<PackagingBoxPage />} />
                            <Route path="/parametros/regiao" element={<RegionPage />} />
                            <Route path="/parametros/unidade-de-medida" element={<UnitOfMeasurePage />} />
                            <Route path="/parametros/cargos" element={<RolesPage />} />
                            <Route path="/parametros/condicao-de-faturamento" element={<BillingConditionPage />} />
                            <Route path="/parametros/situacao-de-cheque" element={<CheckStatusPage />} />
                            <Route path="/parametros/publico-alvo" element={<TargetAudiencePage />} />
                            <Route path="/parametros/modelos-de-projeto" element={<ProjectModelPage />} />
                            <Route path="/parametros/cores" element={<ColorsPage />} />
                            <Route path="/parametros/criterio-de-qualidade" element={<QualityCriteriaPage />} />
                            <Route path="/parametros/embalagem" element={<PackagingPage />} />
                            <Route path="/parametros/motivo-de-contato" element={<ContactReasonSACPage />} />
                            <Route path="/parametros/situacao" element={<SituationSACPage />} />
                            <Route path="/parametros/motivo-devolucao-produto" element={<ProductReturnPage />} />
                            <Route path="/parametros/modelos" element={<ModelsPage />} />
                            <Route path="/parametros/motivo-de-alteracao-de-estoque" element={<StockChangeReasonPage />} />
                            <Route path="/parametros/prefixos" element={<SuffixPage />} />
                            <Route path="/parametros/setup-de-preparo" element={<PreparationSetupPage />} />

                            {/* Tipos Routes */}
                            <Route path="/parametros/tipo/cliente" element={<TypeClientPage />} />
                            <Route path="/parametros/tipo/produto" element={<TypeProductPage />} />
                            <Route path="/parametros/tipo/endereco" element={<TypeAddressPage />} />
                            <Route path="/parametros/tipo/telefone" element={<TypePhonePage />} />
                            <Route path="/parametros/tipo/contato" element={<TypeContactPage />} />
                            <Route path="/parametros/tipo/resultado-contato" element={<TypeContactResultPage />} />
                            <Route path="/parametros/tipo/documento-pagamento" element={<TypePaymentDocPage />} />
                            <Route path="/parametros/tipo/projeto" element={<TypeProjectPage />} />
                            <Route path="/parametros/tipo/funcao" element={<TypePositionPage />} />
                            <Route path="/parametros/tipo/natureza" element={<TypeNaturePage />} />
                            <Route path="/parametros/tipo/empresa" element={<TypeBusinessPage />} />
                            <Route path="/parametros/tipo/caixa" element={<TypeBoxPage />} />
                            <Route path="/parametros/tipo/palete" element={<TypePalletPage />} />

                            {/* ============================================ */}
                            {/* TUV ERP SYSTEM ROUTES */}
                            {/* ============================================ */}
                            <Route path="/tuv/dashboard" element={<TUVDashboard />} />
                            <Route path="/tuv/relatorio" element={<TUVReportPage />} />
                            <Route path="/tuv/modelos/checklists" element={<TUVChecklistModelPage />} />
                            <Route path="/tuv/helpdesk" element={<HelpDeskPage />} />
                            <Route path="/tuv/checklist" element={<TUVChecklistPage />} />
                            <Route path="/tuv/permissoes" element={<PermissionsPage />} />
                        </Route>

                        {/* 404 Route */}
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </ChatProvider>
                </NotificationProvider>
            </Router>
            </CallProvider>
        </SettingsProvider>
    );
}

export default App;